import React, { useEffect, useState } from "react";
import { AllTabType } from "src/types/GlobalInterfaces";
import DrawerTitle from "../Drawer/DrawerTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import SimpleInput from "../SimpleInput";
import SelectDropdown, {
  CustomDropdownItemRenderer,
} from "../GlobalComponents/SelectDropdown";
import { Option } from "react-multi-select-component";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import GradientBtn from "../WebsiteComponents/GradientBtn";
import { useDispatch, useSelector } from "react-redux";
import { setUserDetails } from "src/Store/reducer";
import { RootState } from "src/Store/store";
import { ImageBaseUrl, phoneNumberRegex } from "src/lib/constans";
import { capitalize } from "@mui/material";
import {
  checkAnyChanges,
  convertToKebabCase,
  ImageTypeValidate,
  validateAndFilterPhoneNumber,
} from "src/helpers/helperFunctions";
import CustomWebsiteDropdown from "../GlobalComponents/Widget/CustomWebsiteDropdown";

interface IPorps {
  onBack?: () => void;
  setActiveTab?: (name: AllTabType) => void;
  onClose?: () => void;
  forceBack?: boolean;
  onSuccess?: (data: any) => void;
}

const genderOptions: Option[] = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Other", value: "Other" },
];

interface IDefaultItemRendererProps {
  checked: boolean;
  option: Option;
  disabled?: boolean;
  onClick: () => void;
}

const EditProfileScreen: React.FC<IPorps> = ({
  onBack,
  setActiveTab,
  onClose,
  forceBack = false,
  onSuccess,
}) => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const [fName, setFName] = useState(userDetails?.firstName || "");
  const [lName, setLName] = useState(userDetails?.lastName || "");
  const [dob, setDob] = useState(userDetails?.dob || "");
  const [gender, setGender] = useState<Option[]>(
    userDetails?.gender
      ? [
          {
            label: capitalize(userDetails.gender || ""),
            value: userDetails.gender || "",
          },
        ]
      : []
  );
  const [number, setNumber] = useState(userDetails?.number || "");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    userDetails?.profileImageUrl ? userDetails?.profileImageUrl : null
  );
  const [imageLoader, setImageLoader] = useState<boolean>(false);
  const [saveLoader, setSaveLoader] = useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<{ [key: string]: string }>({});

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files?.[0] || null;

    if (!ImageTypeValidate({ file })) {
      return;
    }

    setProfileImage(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const dispatch = useDispatch();

  const handleApi = async (
    data: { [key: string]: any },
    setLoader: (value: boolean) => void,
    hitBy: string = "button"
  ) => {
    let payload = new FormData();
    for (const key in data) {
      if (data[key]) {
        payload.append(key, data[key]);
      }
    }

    setLoader(true);

    let { res, err } = await httpRequest<{
      message: string;
      customerEdit: any;
    }>({
      path: "/customer/profile-edit",
      method: "put",
      params: payload,
      header: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (res) {
      toast("Profile updated successfully", { type: "success" });
      let data = res.customerEdit;
      setImagePreview(data.profileImageUrl);
      setProfileImage(null);
      dispatch(setUserDetails(data));
      if (onBack && forceBack && hitBy === "button") {
        onBack();
        onSuccess?.(data);
      }
    } else {
      toast("Error updating profile", { type: "error" });
    }
    setLoader(false);
  };

  const validateForm = () => {
    let err: { [key: string]: string } = {};
    if (!fName) {
      err.fName = "First name is required";
    } else if (fName.length < 3) {
      err.fName = "First name must be at least 3 characters long";
    }

    if (!lName) {
      err.lName = "Last name is required";
    } else if (lName.length < 3) {
      err.lName = "Last name must be at least 3 characters long";
    }

    if (!dob) {
      err.dob = "Date of birth is required";
    } else {
      const selectedDate = new Date(dob);
      const today = new Date();
      
      // Check if date is in future
      if (selectedDate > today) {
        err.dob = "Date of birth cannot be in the future";
      }
      // Check if age is more than 100 years
      else if (selectedDate < new Date(today.getFullYear() - 100, today.getMonth(), today.getDate())) {
        err.dob = "Date of birth cannot be more than 100 years ago";
      }
      // Check if age is less than 15 years
      else if (selectedDate > new Date(today.getFullYear() - 15, today.getMonth(), today.getDate())) {
        err.dob = "You must be at least 15 years old";
      }
    }
    if (!gender.length) {
      err.gender = "Gender is required";
    }

    if (!number) {
      err.number = "Phone number is required";
    } else if (!phoneNumberRegex.test(number)) {
      err.number = "Please enter a valid phone number";
    }
    setError(err);
    return Object.keys(err).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      let payload: { [key: string]: any } = {};

      if (fName) payload.firstName = fName;
      if (lName) payload.lastName = lName;
      if (dob) payload.dob = dob;
      if (gender.length) payload.gender = gender[0]?.value?.toLowerCase();
      if (number) payload.number = number;
      if (profileImage && imagePreview) payload.profileImageUrl = profileImage;
      handleApi(payload, setSaveLoader, "button");
    }
  };
  return (
    <div>
      <DrawerTitle title="Edit Profile" backBtn onBack={onBack} />
      <div className="min-h-screen bg-[#F6FAFF] flex flex-col items-center justify-start 2xl:py-4 py-2 2xl:px-4 px-2">
        <div className="relative lg:w-[8vw] lg:h-[8vw] w-[100px] h-[100px] rounded-full bg-[#FFFFFF] border-4 border-[#00443F] flex items-center justify-center">
          {!imagePreview ? (
            <svg
              className="w-16 h-16 text-[#004B45]"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.4c-3.2 0-9.5 1.6-9.5 4.9v2.4h19V19.3c0-3.3-6.3-4.9-9.5-4.9z" />
            </svg>
          ) : (
            <img
              src={imagePreview}
              alt="Profile"
              className="w-full h-full rounded-full object-cover"
            />
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute top-0 right-0 2xl:w-[34px] 2xl:h-[34px] w-[24px] h-[24px] bg-white cursor-pointer rounded-full p-1 border border-[#00443F] flex items-center justify-center "
            id="edit-profile-image-btn"
          >
            <input
              type="file"
              onChange={handleImageChange}
              hidden
              ref={fileInputRef}
            />
            <FontAwesomeIcon
              icon={faPenToSquare}
              className="text-[#00443F] font-size-16px"
            />
          </button>
        </div>

        <div className="w-full my-5 bg-white rounded-[10px] shadow 2xl:p-4 p-2 border border-[#E6E6E6]">
          <h2
            className="flex items-center gap-2 pb-2 mb-2 font-size-18px font-Poppins-Medium text-[#202020]"
            style={{
              borderBottom: "1px solid #E6E6E6",
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            Edit Details
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 2xl:gap-4">
            <div>
              <SimpleInput
                value={fName}
                onChange={(value) => {
                  setFName(value);
                  setError({ ...error, fName: "" });
                }}
                type="text"
                placeholder="Enter your first name"
                isLabel={true}
                labelText="First Name"
                isRequired
                inputMode="text"
                id="edit-profile-first-name-input"
                newUI={true}
                error={error.fName}
                maxLength={30}
              />
            </div>
            <div>
              <SimpleInput
                value={lName}
                onChange={(value) => {
                  setLName(value);
                  setError({ ...error, lName: "" });
                }}
                type="text"
                placeholder="Enter your last name"
                isLabel={true}
                labelText="Last Name"
                isRequired
                inputMode="text"
                id="edit-profile-last-name-input"
                newUI={true}
                error={error.lName}
                maxLength={30}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 2xl:gap-4">
            <div>
              <SimpleInput
                value={dob}
                onChange={(value) => {
                  setDob(value);
                  setError({ ...error, dob: "" });
                }}
                type="date"
                max={(() => {
                  const today = new Date();
                  const maxDate = new Date(
                    today.getFullYear() - 15,
                    today.getMonth(),
                    today.getDate()
                  );
                  return maxDate.toISOString().split("T")[0];
                })()}
                isLabel={true}
                labelText="Date of Birth"
                isRequired
                newUI
                id="edit-profile-dob-input"
                error={error.dob}
              />
            </div>
            <div>
              <CustomWebsiteDropdown
                options={genderOptions}
                data={gender}
                setData={(value) => {
                  setGender(value);
                  setError({ ...error, gender: "" });
                }}
                labelText="Gender"
                isRequired
                placeholder="Select Gender"
                optionId={(item: Option, index: number) =>
                  `option-${item.value}`
                }
                dropdownMainId="edit-profile-gender-input"
                newUI
                clearId="edit-profile-gender-input"
                showRadioBtn
                error={error.gender}
                // withoutMargin
                withoutTopMargin
              />
            </div>
          </div>
          <SimpleInput
            value={number}
            onChange={(value) => {
              const { filteredValue, errorMessage } = validateAndFilterPhoneNumber(value);
              setNumber(filteredValue);
              setError((prev) => ({
                ...prev,
                number: errorMessage,
              }));
            }}
            name="number"
            maxLength={14}
            minLength={11}
            isLabel={true}
            labelText="Phone"
            isRequired
            newUI
            id="edit-profile-phone-input"
            error={error.number}
            placeholder="e.g., 03001234567 or +923001234567"
          />
        </div>
        <GradientBtn
          onPress={handleSave}
          loader={saveLoader}
          ButtonText="Save Details"
          disabled={
            !checkAnyChanges(userDetails, {
              firstName : fName,
              lastName :lName,
              dob :dob,
              gender :gender[0]?.value?.toLowerCase(),
              number :number,
              profileImageUrl : imagePreview,
            })
          }
          btnExtraStyle={{
            width: "100%",
          }}
        />
      </div>
    </div>
  );
};

export default EditProfileScreen;
